import React from "react";
import { useTranslation } from "react-i18next";
import clsx from "clsx";

export type ProductStatus =
  | "all"
  | "active"
  | "draft"
  | "disabled"
  | "banned"
  | "deleted"
  | "draftMode"
  | "soldOut"
  | "inProcess"
  | "failedPublish";

export interface StatusTab {
  key: ProductStatus;
  label: string;
  count?: number;
}

export interface StatusTabsProps {
  activeStatus: ProductStatus;
  onStatusChange: (status: ProductStatus) => void;
  tabs?: StatusTab[];
  className?: string;
  showCounts?: boolean;
  disabled?: boolean;
  "data-testid"?: string;
}

const defaultTabs: StatusTab[] = [
  { key: "all", label: "All Products" },
  { key: "active", label: "Active" },
  { key: "draft", label: "Draft" },
  { key: "disabled", label: "Disabled" },
];

function StatusTabs({
  activeStatus,
  onStatusChange,
  tabs = defaultTabs,
  className = "",
  showCounts = true,
  disabled = false,
  "data-testid": dataTestId,
}: StatusTabsProps) {
  const { t } = useTranslation();

  const getTabLabel = (tab: StatusTab): string => {
    const translationKey = `Product.Status.${
      tab.key.charAt(0).toUpperCase() + tab.key.slice(1)
    }`;
    return t(translationKey, tab.label);
  };

  const getTabCountDisplay = (count?: number): string => {
    if (!showCounts || count === undefined || count === null) return "";
    return `(${(count || 0).toLocaleString()})`;
  };

  const handleTabClick = (status: ProductStatus) => {
    if (!disabled && status !== activeStatus) {
      onStatusChange(status);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent, status: ProductStatus) => {
    if (!disabled && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onStatusChange(status);
    }
  };

  return (
    <div
      className={clsx("tw-w-full tw-border-b tw-border-gray-200", className)}
      data-testid="status-tabs-container"
    >
      {/* Horizontal scrolling container */}
      <div
        className={clsx(
          "tw-flex tw-gap-6 -tw-mb-px tw-overflow-x-auto tw-scrollbar-hide tw-pb-0",
          "tw-scroll-smooth",
          // Ensure tabs don't shrink below their content
          "[&>*]:tw-flex-shrink-0"
        )}
      >
        {tabs.map((tab) => {
          const isActive = tab.key === activeStatus;
          const tabLabel = getTabLabel(tab);
          const countDisplay = getTabCountDisplay(tab.count);

          return (
            <button
              key={tab.key}
              type="button"
              className={clsx(
                "tw-flex tw-items-center tw-justify-center tw-border-b-2 tw-pb-2 sm:tw-pb-3 tw-pt-2 sm:tw-pt-1 tw-whitespace-nowrap tw-min-w-fit tw-px-3 sm:tw-px-1 tw-bg-transparent tw-border-none tw-outline-none",
                {
                  "tw-border-primary tw-text-primary": isActive,
                  "tw-border-transparent tw-text-gray-500 hover:tw-text-gray-800 hover:tw-border-gray-300":
                    !isActive && !disabled,
                  "tw-text-gray-400 tw-cursor-not-allowed": disabled,
                  "tw-cursor-pointer": !disabled,
                }
              )}
              onClick={() => handleTabClick(tab.key)}
              onKeyDown={(e) => handleKeyDown(e, tab.key)}
              data-testid={`status-tab-${tab.key}`}
              aria-selected={isActive}
              tabIndex={disabled ? -1 : 0}
              disabled={disabled}
            >
              <p
                className={clsx("tw-text-sm tw-whitespace-nowrap", {
                  "tw-font-bold": isActive,
                  "tw-font-semibold": !isActive,
                })}
              >
                {tabLabel}
                {countDisplay && (
                  <span className="tw-ml-1 tw-text-xs tw-opacity-75 tw-hidden">
                    {countDisplay}
                  </span>
                )}
              </p>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default StatusTabs;
